import {AggregationOption} from '__generated__/__types__';
import classNames from 'classnames';
import dynamic from 'next/dynamic';
import {useEffect, useRef, useState} from 'react';
import Select, {MenuListProps, ValueContainerProps, components} from 'react-select';

const BasicFilterOption = dynamic(() => import('./category-filter-option/basic-option'));
const ColorFilterOption = dynamic(() => import('./category-filter-option/color-option'));
const RatingFilterOption = dynamic(() => import('./category-filter-option/rating-option'));

const {ValueContainer, MenuList} = components;

const CustomValueContainer = ({children, ...props}: ValueContainerProps) => {
    return (
        <ValueContainer {...props}>
            {props.hasValue && <p className="filter__custom-placeholder">{props.selectProps.placeholder}</p>}
            {children}
        </ValueContainer>
    );
};

interface FilterOptionMenuListProps extends MenuListProps {
    attributeCode?: string;
    onClearSelectedOption: () => void;
    onSave: () => void;
}

const FilterOptionMenuList = ({
    children,
    attributeCode,
    onClearSelectedOption,
    onSave,
    ...props
}: FilterOptionMenuListProps) => {
    // handle display menu right to left
    const menuRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (menuRef.current) {
            const position = menuRef.current.getBoundingClientRect();
            const menuElement = document.getElementById(`react-select-${attributeCode}-listbox`);
            if (menuElement && window.innerWidth - position.left < 480) {
                menuElement.style.right = '0';
            }
        }
    }, []);
    return (
        <div ref={menuRef}>
            <MenuList {...props} className="max-h-[400px] w-[480px]">
                <div className={classNames('flex flex-row flex-wrap gap-4 px-2')}>{children}</div>
                <div className="mt-5 flex flex-row items-center justify-center gap-6 font-bold">
                    <button
                        aria-label="Xóa tùy chọn đã chọn"
                        className="h-[38px] w-[180px] rounded-full border-2 border-solid border-KPZ_Gray-04 text-KPZ_Gray-04 transition-opacity hover:opacity-90"
                        onClick={onClearSelectedOption}>
                        Bỏ chọn
                    </button>
                    <button
                        aria-label="Lưu kết quả lọc"
                        className="h-[38px] w-[180px] rounded-full bg-KPZ_Cam text-white transition-colors hover:bg-orange-500/90"
                        onClick={onSave}>
                        Xem kết quả
                    </button>
                </div>
            </MenuList>
        </div>
    );
};

const MAP_OPTION_TO_SELECT: Record<string, any> = {
    basic: BasicFilterOption,
    color: ColorFilterOption,
    ratings_summary: RatingFilterOption,
};

interface IProps {
    label: string;
    options: AggregationOption[];
    onSave?: (value: string[] | string) => void;
    isMulti?: boolean;
    attributeCode?: string;
    defaultValue?: any;
}

function CategoryFilterItem({label, options, isMulti = true, attributeCode, defaultValue, onSave}: Readonly<IProps>) {
    const [selectedItems, setSelectedItems] = useState<AggregationOption[] | AggregationOption>(defaultValue ?? []);

    const handleOnChange = (valueOption: any) => {
        setSelectedItems(valueOption);
    };
    const handleClearSelectedItems = () => {
        setSelectedItems([]);
    };

    const handleOnSave = () => {
        if (onSave) {
            if (Array.isArray(selectedItems)) {
                const valuesArray: string[] = selectedItems.map(item => item.value);
                onSave(valuesArray);
            } else {
                onSave(selectedItems?.value);
            }
        }
    };

    const OptionComponent = MAP_OPTION_TO_SELECT.hasOwnProperty(attributeCode!)
        ? MAP_OPTION_TO_SELECT[attributeCode!]
        : MAP_OPTION_TO_SELECT['basic'];

    if (attributeCode === 'category_id') return null;

    return (
        <Select
            instanceId={attributeCode}
            components={{
                ValueContainer: CustomValueContainer,
                MenuList: (props: any) => (
                    <FilterOptionMenuList
                        attributeCode={attributeCode}
                        onSave={handleOnSave}
                        onClearSelectedOption={handleClearSelectedItems}
                        {...props}
                    />
                ),
                Option: OptionComponent,
            }}
            styles={{
                menu: (provided: any) => ({
                    ...provided,
                }),
            }}
            value={selectedItems}
            classNamePrefix="filter"
            placeholder={label}
            onChange={handleOnChange}
            isSearchable={false}
            options={options}
            hideSelectedOptions={false}
            closeMenuOnSelect={false}
            isMulti={isMulti}
        />
    );
}

export default CategoryFilterItem;
